import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ToastRef, ToastService } from './toast.service';

type ToastKind = 'success' | 'info' | 'error';

const DEFAULT_DURATION: Record<ToastKind, number> = {
  success: 3000,
  info: 3000,
  error: 6000,
};

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  showSuccess(message: string, duration?: number): ToastRef {
    return this.open('success', message, duration);
  }

  showInfo(message: string, duration?: number): ToastRef {
    return this.open('info', message, duration);
  }

  async showError(message: string, duration?: number): Promise<ToastRef> {
    return this.open('error', await this.translateKey(message), duration);
  }

  async showSuccessTranslate(key: string, duration?: number): Promise<ToastRef> {
    return this.open('success', await this.translateKey(key), duration);
  }

  async showInfoTranslate(key: string, duration?: number): Promise<ToastRef> {
    return this.open('info', await this.translateKey(key), duration);
  }

  async showErrorTranslate(key: string, duration?: number): Promise<ToastRef> {
    return this.open('error', await this.translateKey(key), duration);
  }

  /** Löst mit 'OK' auf, wenn bestätigt wurde - sonst mit '', auch beim Wegklicken. */
  showConfirm(message: string): Promise<string> {
    const ref = this.open('success', message, 5000);
    return new Promise((resolve) => {
      ref.afterDismissed().subscribe((event) => resolve(event.dismissedByAction ? 'OK' : ''));
    });
  }

  private open(kind: ToastKind, message: string, duration?: number): ToastRef {
    return this.toast.open(message, kind === 'error' ? 'X' : 'Ok', {
      panelClass: [`${kind}-toast`],
      horizontalPosition: 'end',
      verticalPosition: 'top',
      duration: duration ?? DEFAULT_DURATION[kind],
    });
  }

  private translateKey(key: string): Promise<string> {
    return firstValueFrom(this.translate.get(key));
  }
}
